import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaComment,
  FaStickyNote,
  FaBug,
  FaEyeSlash
} from 'react-icons/fa';

// Import image
import comment from './assets/images/comment-example1.png';

const HTMLLesson7 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- <h1>Heading 1</h1> -->`;

  const code2 = `<!-- This is a single-line comment -->`;

  const code3 = `<!-- 
  This is a multi-line comment
  It can span across multiple lines
  Very useful for longer explanations
-->`;

  const code4 = `<!DOCTYPE html>
<html>
<head>
    <title>My Website</title>
</head>
<body>
    <!-- Header Section -->
    <header>
        <h1>Welcome to My Site</h1>
    </header>
    
    <!-- Main Content Area -->
    <main>
        <!-- Article Section -->
        <article>
            <h2>Article Title</h2>
            <p>This is the article content.</p>
        </article>
    </main>
    
    <!-- TODO: Add footer section later -->
    <!-- <footer>
        <p>Copyright 2024</p>
    </footer> -->
</body>
</html>`;

  const code5 = `<!-- 
  DEBUGGING EXAMPLE:
  Commenting out problematic code to isolate issues
-->
<!-- <div class="problematic-element"> -->
<div class="working-element">
    <p>This element is currently active</p>
</div>
<!-- <script>console.log('Debugging message');</script> -->`;

  const sections = [
    {
      title: "HTML Comments Introduction",
      content: "Comment tags are used to insert comments in HTML source code that are not displayed by browsers",
      keyPoints: [
        "Comments help document your HTML source code",
        "They are invisible to website visitors",
        "Essential for code organization and maintenance",
        "Useful for leaving notes and reminders"
      ],
      icon: <FaComment className="section-icon" />,
      color: "#2563eb",
      code: code2
    },
    {
      title: "Comment Syntax & Structure", 
      content: "HTML comments use a specific syntax with an exclamation point in the opening tag",
      keyPoints: [
        "Syntax: <!-- comment text -->",
        "Exclamation point in opening tag, not in closing",
        "Can be single-line or multi-line",
        "Browser completely ignores commented content"
      ],
      icon: <FaStickyNote className="section-icon" />,
      color: "#1d4ed8",
      code: code3
    },
    {
      title: "Practical Uses of Comments",
      content: "Comments serve multiple purposes from documentation to debugging and temporary code removal",
      keyPoints: [
        "Document code sections and functionality",
        "Leave reminders and TODOs for future work",
        "Temporarily disable code without deleting",
        "Explain complex code sections"
      ],
      icon: <FaEyeSlash className="section-icon" />,
      color: "#1e40af",
      code: code4
    },
    {
      title: "Debugging with Comments",
      content: "Comments are invaluable for debugging HTML by isolating problematic code sections",
      keyPoints: [
        "Comment out code line by line to find errors",
        "Isolate problematic sections systematically",
        "Preserve code while testing alternatives",
        "Add debugging notes and observations"
      ],
      icon: <FaBug className="section-icon" />,
      color: "#7c3aed",
      code: code5
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 7 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Comments</h1>
              <p className="html-lesson-subtitle">
                Master the art of documenting, debugging, and organizing your HTML code with comments
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Example Section */}
          <div 
            className="html-visual-comment"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaComment className="html-badge-icon" />
                Comment Example
              </div>
              <h3 className="html-visual-title">
                See How Comments Work in Practice
              </h3>
            </div>
            
            <div className="html-comment-demo">
              <div className="html-comment-code">
                <h4>HTML Code with Comments:</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language={language}
                    text={code1}
                    showLineNumbers={lineNumbers}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-comment-visual">
                <h4>Browser Display:</h4>
                <div className="html-browser-preview">
                  {/* Empty because comment is hidden */}
                  <div className="html-empty-preview">
                    <FaEyeSlash className="html-empty-icon" />
                    <p>Comment is hidden in browser</p>
                  </div>
                </div>
              </div>
            </div>
            
            {comment && (
              <div className="html-comment-image">
                <img src={comment} alt="HTML comment example in code editor" />
                <p className="html-image-caption">Real-world comment example in code editor</p>
              </div>
            )}
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for each section */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Code Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Comment Best Practices
              </div>
              <h3 className="html-practices-title">
                Writing Effective HTML Comments
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do This</h4>
                <ul>
                  <li>Use comments to explain <em>why</em> code exists</li>
                  <li>Document complex or non-obvious code sections</li>
                  <li>Leave TODO notes for future improvements</li>
                  <li>Use consistent comment formatting</li>
                  <li>Comment temporarily during debugging</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid This</h4>
                <ul>
                  <li>Don't state the obvious ("this is a div")</li>
                  <li>Avoid leaving outdated comments</li>
                  <li>Don't comment out large blocks permanently</li>
                  <li>Avoid inconsistent comment styles</li>
                  <li>Don't use comments for sensitive information</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional HTML with Comments
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- 
        HEAD SECTION
        Contains metadata, title, and linked resources
    -->
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Professional Website</title>
    
    <!-- TODO: Add favicon link -->
    <!-- <link rel="icon" href="favicon.ico"> -->
</head>
<body>
    <!-- 
        HEADER SECTION
        Main navigation and site branding
    -->
    <header>
        <h1>My Professional Site</h1>
        <!-- Navigation menu -->
        <nav>
            <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#contact">Contact</a></li>
            </ul>
        </nav>
    </header>

    <!-- 
        MAIN CONTENT AREA
        Primary content of the page
    -->
    <main>
        <!-- Hero Section -->
        <section id="hero">
            <h2>Welcome to Our Site</h2>
            <p>This is the main hero section content.</p>
        </section>

        <!-- Features Section -->
        <section id="features">
            <h2>Our Features</h2>
            <div class="feature">
                <h3>Feature One</h3>
                <p>Description of feature one.</p>
            </div>
            <!-- 
                TEMPORARILY DISABLED FOR TESTING:
                <div class="feature">
                    <h3>Feature Two</h3>
                    <p>Description of feature two.</p>
                </div>
            -->
        </section>
    </main>

    <!-- 
        FOOTER SECTION
        Copyright and additional links
    -->
    <footer>
        <p>&copy; 2024 My Professional Site. All rights reserved.</p>
        <!-- TODO: Add social media links -->
    </footer>

    <!-- 
        DEBUGGING NOTES:
        - Check console for any JavaScript errors
        - Test responsive design on mobile devices
        - Verify all links are working
    -->
</body>
</html>`}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML comments?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned how to use HTML comments for documentation, debugging, and code organization. Start adding comments to your projects!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-attributes" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Want to see more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson7;